import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { X, Plus } from 'lucide-react'

import PageHeader from 'src/components/shared/page-header'
import Container from 'src/components/shared/container'
import Loader from 'src/components/shared/loader'
import ZoneLocationPicker from 'src/components/shared/zone-location-picker'
import { useParcelStore } from 'src/store/parcel'
import { addWorkerAssignment, softDeleteWorkerAssignments } from 'src/store/todo/operations'
import { useSyncedStore } from '@syncedstore/react'
import { todoStore } from 'src/store/todo'

type SelectOption = {
  value: string
  label: string
}

type Zone = {
  id: string
  zone_moniker: string
  widget_zone_row?: number
  widget_zone_column?: number
  locations_aggregate?: {
    nodes: {
      id: string
      location_moniker: string
    }[]
  }
}

type Assignment = {
  id: string
  taskId: string
  taskLabel: string
  zoneId: string
  zoneName: string
  locationIds: string[]
  locationNames: string[]
}

const WorkerAssignPage: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const [selectedTask, setSelectedTask] = useState<SelectOption | null>(null)
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
  const [selectedLocations, setSelectedLocations] = useState<Array<{ id: string; location_moniker: string }>>([])
  const [lastSelectedTask, setLastSelectedTask] = useState<string | null>(null)
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [isEditMode, setIsEditMode] = useState(false)

  const teamId = params.teamId!
  const workerId = params.workerId!
  const workerName = searchParams.get('name') || 'Worker'
  const editMode = searchParams.get('edit') === 'true'

  const state = useSyncedStore(todoStore)

  const activeParcel = useParcelStore((state) => state.parcel)

  // Memoize filtered arrays to prevent infinite loops in useEffect
  const zones = React.useMemo(() =>
    state.zones?.filter(z => z.parcelId === activeParcel && z.isActive) || [],
    [state.zones, activeParcel]
  )

  const locations = React.useMemo(() =>
    state.locations?.filter(l => l.parcelId === activeParcel && l.isActive) || [],
    [state.locations, activeParcel]
  )

  const taskTypes = state.taskTypes || []

  // Get task types from Yjs - memoized to prevent re-renders
  const taskOptions: SelectOption[] = React.useMemo(() =>
    taskTypes.map((task) => ({
      value: task.id,
      label: task.taskTypeDescription,
    })), [taskTypes]
  )

  // Auto-select first task on load or use last selected
  useEffect(() => {
    if (taskOptions.length > 0 && !selectedTask) {
      const taskToSelect = taskOptions.find(t => t.value === lastSelectedTask) || taskOptions[0]
      setSelectedTask(taskToSelect)
    }
  }, [taskOptions, selectedTask, lastSelectedTask])

  
  // Initialize edit mode - load existing assignments
  useEffect(() => {
    if (editMode && zones.length > 0) {
      const existingAssignments = state.workerAssignments.filter(
        (a) => a.workerId === workerId && !a.softDeleted
      )

      if (existingAssignments.length > 0) {
        // Group assignments by taskId+zoneId
        const groupedAssignments: { [key: string]: Assignment } = {}

        existingAssignments.forEach((assignment) => {
          const key = `${assignment.taskTypeId}-${assignment.zoneId}`
          const zone = zones.find(
            (z) => z.id === assignment.zoneId
          )
          const task = taskTypes.find((t) => t.id === assignment.taskTypeId)

          if (!groupedAssignments[key]) {
            groupedAssignments[key] = {
              id: `group-${key}`,
              taskId: assignment.taskTypeId,
              taskLabel: task?.taskTypeDescription || assignment.taskTypeId,
              zoneId: assignment.zoneId,
              zoneName: zone?.zoneMoniker || assignment.zoneId,
              locationIds: [],
              locationNames: [],
            }
          }

          if (assignment.locationId) {
            groupedAssignments[key].locationIds.push(assignment.locationId)
            const location = locations.find(
              (l) => l.id === assignment.locationId
            )
            if (location && !groupedAssignments[key].locationNames.includes(location.locationMoniker)) {
              groupedAssignments[key].locationNames.push(location.locationMoniker)
            }
          }
        })

        setAssignments(Object.values(groupedAssignments))
        setIsEditMode(true)
      }
    } else if (!editMode) {
      // Not in edit mode, initialization is done
    }
  }, [editMode, workerId, state.workerAssignments, zones, taskTypes, locations])

  // Sort zones by widget_zone_column then widget_zone_row
  const sortedZones = [...zones]
    .sort((a, b) => {
      const colA = a.widgetZoneColumn || 999
      const colB = b.widgetZoneColumn || 999
      if (colA !== colB) return colA - colB
      return (a.widgetZoneRow || 999) - (b.widgetZoneRow || 999)
    })
    .map((z) => ({
      id: z.id,
      zone_moniker: z.zoneMoniker,
      widget_zone_row: z.widgetZoneRow,
      widget_zone_column: z.widgetZoneColumn,
      locations_aggregate: {
        nodes: locations
          .filter(l => l.zoneId === z.id)
          .map(l => ({
            id: l.id,
            location_moniker: l.locationMoniker
          }))
      }
    }))

  const handleAdd = () => {
    if (selectedTask && selectedZone) {
      setLastSelectedTask(selectedTask.value)
      
      // Check if this task+zone combination already exists
      const existingIndex = assignments.findIndex(
        (a) => a.taskId === selectedTask.value && a.zoneId === selectedZone.id
      )
      
      const newAssignment: Assignment = {
        id: `${Date.now()}-${Math.random()}`,
        taskId: selectedTask.value,
        taskLabel: selectedTask.label,
        zoneId: selectedZone.id,
        zoneName: selectedZone.zone_moniker,
        locationIds: selectedLocations.map(l => l.id),
        locationNames: selectedLocations.map(l => l.location_moniker),
      }
      
      if (existingIndex !== -1) {
        // Replace the existing assignment with the same task+zone
        const updatedAssignments = [...assignments]
        updatedAssignments[existingIndex] = newAssignment
        setAssignments(updatedAssignments)
      } else {
        // Add new assignment
        setAssignments([...assignments, newAssignment])
      }
      
      // Reset selections
      setSelectedZone(null)
      setSelectedLocations([])
    }
  }

  const handleRemoveAssignment = (assignmentId: string) => {
    setAssignments(assignments.filter(a => a.id !== assignmentId))
  }

  const handleConfirmAll = () => {

    if (isEditMode) {
      softDeleteWorkerAssignments(workerId)
    }
    assignments.forEach((assignment) => {
      if (assignment.locationIds.length > 0) {
        assignment.locationIds.forEach((locationId) => {
          addWorkerAssignment(teamId, workerId, assignment.taskId, assignment.zoneId, locationId)
        })
      } else {
        addWorkerAssignment(teamId, workerId, assignment.taskId, assignment.zoneId)
      }
    })
    navigate(-1)
  }

  const isReadyToAdd = selectedTask && selectedZone

  return (
    <div className='min-h-screen w-full flex flex-col'>
      <PageHeader title={`Assign: ${workerName}`} isDetail />
      <Container>
        {zones.length === 0 ? (
          <Loader />
        ) : (
          <div className='flex flex-col gap-4 w-full pb-20'>
            {/* Task Picker - Tappable Buttons */}
            {taskOptions.length > 0 && (
              <div className='overflow-x-auto -mx-4 px-4'>
                <div className='flex gap-3 flex-nowrap'>
                  {taskOptions.map((task) => (
                    <button
                      key={task.value}
                      onClick={() => {
                        setSelectedTask(task)
                        setLastSelectedTask(task.value)
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0 whitespace-nowrap ${
                        selectedTask?.value === task.value
                          ? 'bg-blue-700 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {task.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Zone/Location Picker Section */}
            {selectedTask && sortedZones.length > 0 && (
              <ZoneLocationPicker
                zones={sortedZones}
                selectedZone={selectedZone}
                selectedLocations={selectedLocations}
                onZoneSelect={setSelectedZone}
                onLocationsSelect={setSelectedLocations}
                selectedTaskId={selectedTask.value}
                workerAssignments={state.workerAssignments}
              />
            )}

            {/* Assignment Summary with Add Button */}
            {selectedTask && selectedZone && (
              <button
                onClick={handleAdd}
                disabled={!isReadyToAdd}
                className='w-full p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors flex items-center justify-between'
              >
                <div className='flex flex-col gap-1 text-sm text-blue-800 text-left'>
                  <p>
                    <span className='font-medium'>Task:</span> {selectedTask.label} <span className='font-medium ml-2'>Zone:</span> {selectedZone.zone_moniker}
                  </p>
                  {selectedLocations.length > 0 && (
                    <p>
                      <span className='font-medium'>Locations:</span> {selectedLocations.map(l => l.location_moniker).join(', ')}
                    </p>
                  )}
                </div>
                <Plus size={24} className='text-blue-700 flex-shrink-0 ml-2' />
              </button>
            )}

            {/* Add Button - Hidden */}
            {false && (
              <button
                onClick={handleAdd}
                disabled={!isReadyToAdd}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isReadyToAdd
                    ? 'bg-blue-700 text-white hover:bg-blue-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Add Assignment
              </button>
            )}

            {/* Assignments List */}
            {assignments.length > 0 && (
              <div className='flex flex-col gap-2 pt-2'>
                <label className='text-sm font-semibold text-gray-700'>Assignments ({assignments.length})</label>
                {assignments.map((assignment) => (
                  <div key={assignment.id} className='flex items-start justify-between gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200'>
                    <div className='flex-1 flex flex-col gap-1 text-sm'>
                      <p><span className='font-medium'>Task:</span> {assignment.taskLabel} <span className='font-medium ml-2'>Zone:</span> {assignment.zoneName}</p>
                      {assignment.locationNames.length > 0 && (
                        <p><span className='font-medium'>Locations:</span> {assignment.locationNames.join(', ')}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      className='flex-shrink-0 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors'
                      title='Remove assignment'
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className='flex gap-3 pt-2'>
              <button
                onClick={() => navigate(-1)}
                className='flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAll}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors 'bg-green-700 text-white hover: bg-green-700`}
              >
                Confirm All
              </button>
            </div>
          </div>
        )}
      </Container>
    </div>
  )
}

export default WorkerAssignPage
